import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'

import { LOGIN_PATH } from 'account/login/Login'
import { Layout as AntLayout, Menu } from 'antd'
import { NavigationMapping } from 'docReviewDashboard'

import { User } from 'core/application/account/loginUser'
import { Permissions } from 'core/application/account/permissions'

import useLoginUser from 'common/hooks/useLoginUser'

import NotFound from 'errorPages/NotFound'

import ChangePassword from '../account/changePassword'
import HeaderUserSection from '../common/components/layout/HeaderUserSection'
import InternalToolsHeaderSwitch from '../common/components/navigation/InternalToolsHeaderSwitch/InternalToolsHeaderSwitch'
import useSharedState, { SharedStateKeys } from '../common/hooks/useSharedState'
import { ROUTE_PATHS } from '../common/routePaths'
import AppConfigurations from './appConfigurations'
import AuditSearch, { AUDIT_SEARCH_PATH } from './auditSearch'
import CsiSearch from './csiSearch'
import { CSI_SEARCH_PATH } from './csiSearch/searchCustomers'
import DefaultExpenseTypes from './defaultExpenseTypes'
import EmailTemplates from './emailTemplates'
import AddEmailTemplate from './emailTemplates/addEmailTemplate'
import EditEmailTemplate from './emailTemplates/editEmailTemplate'
import InternalUsers, { INTERNAL_USERS_PATH } from './internalUsers'
import Migration, { MIGRATION_PATH } from './migration'
import { PERFORMANCE_TRACKING_PATH } from './performanceTracking'
import QaUtils, { QA_UTILS_PATH } from './qaUtils'
import Reports, { REPORTS_PATH } from './reports'
import ScheduledJobs, { SCHEDULED_JOBS_PATH } from './scheduledJobs'
import SearchEmails, { SEARCH_EMAILS_PATH } from './searchEmails'
import SearchUserActionHistory, { SEARCH_USER_ACTION_HISTORY_PATH } from './searchUserActionHistory'
import EmailsSuppressionList, { SUPPRESSION_LIST_PATH } from './suppressionList'

import './styles.less'

export const CSI_DASHBOARD_PATH = '/csi-dashboard'

type CsiProps = {}

const CsiDashboard: React.FC<CsiProps> = () => {
  const location = useLocation()
  const history = useHistory()
  const { t: translate } = useTranslation()
  const [user] = useLoginUser()

  const [activeKey, setActiveKey] = useState<string[] | undefined>()
  const [isProduction] = useSharedState<boolean>(SharedStateKeys.IsProduction)

  const isUserAllowedToEnterDashboard = (user: User): boolean => {
    return user !== null && user.permissions.includes(Permissions.AccessCsiDashboard)
  }

  useEffect(() => {
    if (!isUserAllowedToEnterDashboard(user)) history.push(LOGIN_PATH)
  }, [])

  useEffect(() => {
    const pathname = location.pathname
    const split = pathname.split('/')[2]
    const res = navigationPathMapping.get(split) || navigationPathMapping.get(pathname)
    setActiveKey(res ? [res.toString()] : [NaviationMapping.CsiSearch.toString()])
  }, [location])

  return (
    user && (
      <div data-testid={'csi-wrapper'}>
        <AntLayout>
          <Menu selectedKeys={activeKey} mode="horizontal">
            <InternalToolsHeaderSwitch defaultValue={Permissions.AccessCsiDashboard} />
            {getNavigationMenu(user.permissions, isProduction)
              .sort((first, second) => first.key - second.key)
              .map((item: MenuItem) => (
                <Menu.Item key={item.key} onClick={() => history.push(item.path)}>
                  {translate(item.name)}
                </Menu.Item>
              ))}
            <Menu.Item key={18} onClick={() => {}} className="header_right_section  mr-64 no_hover_styles ">
              <div className="flex__space_between flex__space_between_centered ">
                <HeaderUserSection user={user} userInfo={user} />
              </div>
            </Menu.Item>
          </Menu>
          <AntLayout className="dashboard_body">
            <AntLayout className="dashboard-layout-content">
              <Switch>
                <Route
                  key={NaviationMapping.ChangePassword}
                  path={ROUTE_PATHS.CSI_CHANGE_PASSWORD}
                  component={ChangePassword}
                />
                <Route
                  key={NaviationMapping.AddEmailTemplates}
                  path={ROUTE_PATHS.ADD_EMAIL_TEMPLATES}
                  component={AddEmailTemplate}
                />
                <Route
                  exact={true}
                  key={NaviationMapping.EditEmailTemplates}
                  path={`${ROUTE_PATHS.EDIT_EMAIL_TEMPLATES}/:templateId`}
                  component={EditEmailTemplate}
                />
                {getNavigationMenu(user.permissions)
                  .sort((first, second) => second.key - first.key)
                  .map((item: MenuItem) => (
                    <Route key={item.key} path={item.path} component={item.component} />
                  ))}
                <Route>
                  <NotFound />
                </Route>
              </Switch>
            </AntLayout>
          </AntLayout>
        </AntLayout>
      </div>
    )
  )
}

export default CsiDashboard

type MenuItem = {
  name: string
  key: number
  path: string
  icon?: string
  component: any
}

enum NaviationMapping {
  // Dashboard = 1,
  CsiSearch = 2,
  SearchEmails = 3,
  ChangePassword = 4,
  ScheduledJobs = 5,
  InternalUsers = 6,
  PerformanceTracking = 7,
  AuditSearch = 8,
  QaUtils = 9,
  Migration = 10,
  Marketplace = 11,
  Reports = 12,
  SuppressionList = 13,
  Configurations = 14,
  SearchUserActionHistory = 15,
  ExpenseTypes = 16,
  EmailTemplates = 17,
  AddEmailTemplates = 18,
  EditEmailTemplates = 19,
}

const navigationPathMapping = new Map<string, number>([
  // [DASHBOARD_PATH, NaviationMapping.Dashboard],
  [CSI_SEARCH_PATH, NaviationMapping.CsiSearch],
  [SEARCH_EMAILS_PATH, NaviationMapping.SearchEmails],
  [SEARCH_USER_ACTION_HISTORY_PATH, NaviationMapping.SearchUserActionHistory],
  [SCHEDULED_JOBS_PATH, NaviationMapping.ScheduledJobs],
  [INTERNAL_USERS_PATH, NaviationMapping.InternalUsers],
  [PERFORMANCE_TRACKING_PATH, NaviationMapping.PerformanceTracking],
  [AUDIT_SEARCH_PATH, NaviationMapping.AuditSearch],
  [QA_UTILS_PATH, NaviationMapping.QaUtils],
  [MIGRATION_PATH, NaviationMapping.Migration],
  [ROUTE_PATHS.CSI_MARKETPLACE, NaviationMapping.Marketplace],
  [REPORTS_PATH, NaviationMapping.Reports],
  [SUPPRESSION_LIST_PATH, NaviationMapping.SuppressionList],
  [ROUTE_PATHS.CSI_CONFIGURATIONS, NaviationMapping.Configurations],
  [ROUTE_PATHS.CSI_EXPENSE_TYPES, NaviationMapping.ExpenseTypes],
  [ROUTE_PATHS.EMAIL_TEMPLATES, NaviationMapping.EmailTemplates],
])

const getNavigationMenu = (permissions: Permissions[], isProduction: boolean = false): MenuItem[] => {
  const menus = [
    // {
    //   name: 'csi-dashboard.navigation.dashboard',
    //   key: NaviationMapping.Dashboard,
    //   path: `${CSI_DASHBOARD_PATH}/${DASHBOARD_PATH}`,
    //   component: Dashboard,
    // },
    {
      name: 'csi-dashboard.navigation.csi-search',
      key: NaviationMapping.CsiSearch,
      path: `${CSI_DASHBOARD_PATH}/${CSI_SEARCH_PATH}`,
      component: CsiSearch,
    },
    {
      name: 'csi-dashboard.navigation.search-emails',
      key: NaviationMapping.SearchEmails,
      path: `${CSI_DASHBOARD_PATH}/${SEARCH_EMAILS_PATH}`,
      component: SearchEmails,
    },
    {
      name: 'csi-dashboard.navigation.search-user-action-history',
      key: NaviationMapping.SearchUserActionHistory,
      path: `${CSI_DASHBOARD_PATH}/${SEARCH_USER_ACTION_HISTORY_PATH}`,
      component: SearchUserActionHistory,
    },
    // {
    //   name: 'csi-dashboard.navigation.performance-tracking',
    //   key: NaviationMapping.PerformanceTracking,
    //   path: `${CSI_DASHBOARD_PATH}/${PERFORMANCE_TRACKING_PATH}`,
    //   component: PerformanceTracking,
    // },
    {
      name: 'csi-dashboard.navigation.audit-search',
      key: NaviationMapping.AuditSearch,
      path: `${CSI_DASHBOARD_PATH}/${AUDIT_SEARCH_PATH}`,
      component: AuditSearch,
    },
    {
      name: 'csi-dashboard.navigation.suppression-list',
      key: NaviationMapping.SuppressionList,
      path: `${CSI_DASHBOARD_PATH}/${SUPPRESSION_LIST_PATH}`,
      component: EmailsSuppressionList,
    },
    {
      name: 'csi-dashboard.navigation.configurations',
      key: NaviationMapping.Configurations,
      path: ROUTE_PATHS.CSI_CONFIGURATIONS,
      component: AppConfigurations,
    },
    {
      name: 'csi-dashboard.navigation.expense-types',
      key: NaviationMapping.ExpenseTypes,
      path: ROUTE_PATHS.CSI_EXPENSE_TYPES,
      component: DefaultExpenseTypes,
    },
  ]

  if (!isProduction) {
    menus.push({
      name: 'csi-dashboard.navigation.qa-utils',
      key: NaviationMapping.QaUtils,
      path: `${ROUTE_PATHS.CSI_QA_UTILS}`,
      component: QaUtils,
    })
  }

  if (permissions.includes(Permissions.AccessScheduledJobs)) {
    menus.push({
      name: 'csi-dashboard.navigation.scheduled-jobs',
      key: NaviationMapping.ScheduledJobs,
      path: `${CSI_DASHBOARD_PATH}/${SCHEDULED_JOBS_PATH}`,
      component: ScheduledJobs,
    })
  }

  if (permissions.includes(Permissions.AccessInternalUsers)) {
    menus.push({
      name: 'csi-dashboard.navigation.internal-users',
      key: NaviationMapping.InternalUsers,
      path: `${CSI_DASHBOARD_PATH}/${INTERNAL_USERS_PATH}`,
      component: InternalUsers,
    })
  }

  if (permissions.includes(Permissions.AccessMigration)) {
    menus.push({
      name: 'csi-dashboard.navigation.migration',
      key: NaviationMapping.Migration,
      path: `${CSI_DASHBOARD_PATH}/${MIGRATION_PATH}`,
      component: Migration,
    })
  }

  if (permissions.includes(Permissions.AccessReports)) {
    menus.push({
      name: 'csi-dashboard.navigation.reports',
      key: NaviationMapping.Reports,
      path: `${CSI_DASHBOARD_PATH}/${REPORTS_PATH}`,
      component: Reports,
    })
  }

  if (permissions.includes(Permissions.AccessMigration)) {
    menus.push({
      name: 'csi-dashboard.navigation.email-templates',
      key: NaviationMapping.EmailTemplates,
      path: ROUTE_PATHS.EMAIL_TEMPLATES,
      component: EmailTemplates,
    })
  }
  // if (permissions.includes(Permissions.AccessCsiDashboard)) {
  //   menus.push({
  //     name: 'csi-dashboard.navigation.marketplace',
  //     key: NaviationMapping.Marketplace,
  //     path: ROUTE_PATHS.CSI_MARKETPLACE,
  //     component: AdvertisementTable,
  //   })
  // }

  return menus
}
